// 26 ADM-102, the row for 10: the screen "Simulation" (10 §8; decision record 0071). Owner; an analyst
// reads its dashboards. One read, ops.admin_simulation(): the engine's versions with what stops each
// one (10 SIM-004, SIM-203), the sixteen named proofs of 31 TST-180 with their latest runs, and the
// calibration runs. Nothing here changes the game by itself:
// - "Activate" and "Roll back" are shown switched off, with the reason: 05 gives the approval queue no
//   request for a version, so this build cannot send one (listed for the lead). The database refuses a
//   version that a proof stops in any case (core.sim_versions_activation_guard).
// - "Run now" (SIM-203) is shown switched off, with what it would do: there is no calibration worker
//   in this build. Runs are made with tools/calibrate and stored in ops.proof_runs.
// - The parameter editor (10 §8) only files a proposal, config.propose, into the approval queue, and
//   only for the engine's ⚖ numbers, which never take effect without an owner's approval (ADM-020).
// - The thought line (ADM-095 point 2; SIM-297): the judgement run's twelve shares beside the line.
import type { SupabaseClient } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';
import { ProofState, stateOfRun } from '../components/ProofState';
import { Breakable, Json } from '../components/Text';
import { ThoughtTable } from '../components/ThoughtTable';
import { adminText } from '../lib/copy';
import { codeOf, messageFor, runCommand } from '../lib/commands';
import { pct, PROOF_NAMES, thoughtName } from '../lib/fairplay';
import { capitalWords, PATH_BREAKS } from '../lib/tidy';

export interface ProofRun {
  proof_key: string;
  run_at: string;
  commit_sha: string;
  sim_version: string | null;
  size: 'short' | 'nightly' | 'full' | 'gate';
  pass_line: Record<string, unknown>;
  measured: unknown;
  passed: boolean;
  report_uri: string | null;
}
export interface Block {
  proof_key: string;
  state: 'not_run' | 'red' | 'thought_under_line' | 'drift';
  thoughts?: string[];
  line?: number;
  version?: string;
  run_at?: string;
}
export interface SimVersion {
  version: string;
  is_default: boolean;
  released_at: string | null;
  retired_at: string | null;
  notes: string | null;
  params_hash: string | null;
  blocking: Block[];
}
export interface ProofRow {
  proof_key: string;
  latest: ProofRun | null;
  counted: ProofRun | null;
  gates_stage_2: boolean;
}
export interface CalibrationRun {
  id: string;
  version: string;
  seed_set: string;
  started_at: string;
  finished_at: string | null;
  pass: boolean | null;
}
export interface CalibrationResult {
  band: string;
  metric: string;
  target: number | null;
  tolerance: number | null;
  observed: number | null;
  passed: boolean | null;
  advisory: boolean;
}
export interface SimulationData {
  versions: SimVersion[];
  proofs: ProofRow[];
  calibration_runs: CalibrationRun[];
  calibration_results: CalibrationResult[];
  thought_line: number | null;
}
export interface EngineKey {
  key: string;
  is_fairness: boolean;
  min_value: number | null;
  max_value: number | null;
  owner_file: string | null;
}

const proofName = (k: string) => PROOF_NAMES[k] ?? k;

/** What stops a version, in plain words (SIM-004, SIM-203; ADM-095). */
export function blockText(b: Block): string {
  switch (b.state) {
    case 'not_run':
      return adminText('sim.block_not_run', { proof: proofName(b.proof_key) });
    case 'red':
      return adminText('sim.block_red', { proof: proofName(b.proof_key) });
    case 'thought_under_line':
      return adminText('sim.block_thought', {
        proof: proofName(b.proof_key),
        n: b.thoughts?.length ?? 0,
        names: (b.thoughts ?? []).map(thoughtName).join(', '),
        line: pct(b.line ?? 0.05),
      });
    case 'drift':
      return adminText('sim.block_drift', { version: b.version ?? '—' });
  }
}

/**
 * The lines under "What stops it": every proof with no full run on the version is named in one
 * line (a new candidate has none, and a line for each would bury what has run and failed), then
 * each other block on its own line, in the server's order.
 */
export function stopLines(blocking: readonly Block[]): string[] {
  const notRun = blocking.filter((b) => b.state === 'not_run');
  const rest = blocking.filter((b) => b.state !== 'not_run').map(blockText);
  if (notRun.length < 2) return [...notRun.map(blockText), ...rest];
  return [
    adminText('sim.block_not_run_many', {
      n: notRun.length,
      proofs: notRun.map((b) => proofName(b.proof_key)).join(', '),
    }),
    ...rest,
  ];
}

export type VersionState = 'in_use' | 'retired' | 'stopped' | 'clear';
export function versionState(v: SimVersion): VersionState {
  if (v.is_default) return 'in_use';
  if (v.retired_at) return 'retired';
  return v.blocking.length > 0 ? 'stopped' : 'clear';
}

const when = (iso: string) => new Date(iso);

/** One run of a proof: its state, its size, when and on which version. */
function RunLine({ run, label }: { run: ProofRun | null; label: string }) {
  return (
    <p className="run-line">
      <span className="run-label">{label}</span> <ProofState state={stateOfRun(run)} />{' '}
      {run && (
        <span className="muted">
          {adminText('sim.run_line', {
            size: run.size,
            at: when(run.run_at),
            version: run.sim_version ?? '—',
          })}
        </span>
      )}
    </p>
  );
}

/** A run's own words, when it says it measured nothing: introduced, and shown as written. */
function RunSays({ words }: { words: string }) {
  return (
    <p className="note">
      <span className="run-label">{adminText('sim.run_says')}</span> <span>{words}</span>
    </p>
  );
}

/** What a run measured: its own words when it says it measured nothing, else its figures. */
function Measured({ run }: { run: ProofRun }) {
  if (typeof run.measured === 'string') return <RunSays words={run.measured} />;
  return (
    <details>
      <summary>{adminText('sim.measured_summary')}</summary>
      <Json v={run.measured} />
      {run.report_uri && (
        <p className="muted">
          {adminText('sim.report_kept')}{' '}
          <code>
            <Breakable text={run.report_uri} at={PATH_BREAKS} whole />
          </code>
        </p>
      )}
    </details>
  );
}

function Versions({ data }: { data: SimulationData }) {
  return (
    <section aria-labelledby="sim-v" className="block">
      <h3 id="sim-v">{adminText('sim.versions_heading', { n: data.versions.length })}</h3>
      {data.versions.length === 0 && <p className="muted">{adminText('sim.no_versions')}</p>}
      <ul className="plain grid">
        {/* the version in use first, then the others in the server's order (newest first) */}
        {[
          ...data.versions.filter((v) => v.is_default),
          ...data.versions.filter((v) => !v.is_default),
        ].map((v) => {
          const s = versionState(v);
          return (
            <li key={v.version} className="card">
              <p className="title-row">
                <strong className="version">{v.version}</strong>{' '}
                <span
                  className={
                    s === 'in_use' ? 'chip ok' : s === 'stopped' ? 'chip bad' : 'chip none'
                  }
                >
                  {adminText(`sim.state_${s}`)}
                </span>
              </p>
              {v.notes && <p className="muted">{v.notes}</p>}
              {s === 'stopped' && (
                <>
                  <p className="sub">{adminText('sim.stops_heading')}</p>
                  <ul className="stops">
                    {stopLines(v.blocking).map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                </>
              )}
              {s === 'clear' && <p>{adminText('sim.nothing_stops')}</p>}
              {/* Each button sits on the version it would act on; both are off (the reason is below). */}
              {(s === 'stopped' || s === 'clear') && (
                <p className="actions">
                  <button type="button" disabled aria-describedby="sim-activate-why">
                    {adminText('sim.activate')}
                  </button>
                </p>
              )}
              {s === 'in_use' && (
                <p className="actions">
                  <button type="button" disabled aria-describedby="sim-activate-why">
                    {adminText('sim.roll_back')}
                  </button>
                </p>
              )}
            </li>
          );
        })}
      </ul>
      <p id="sim-activate-why" className="muted">
        {adminText('sim.activate_off')}
      </p>
    </section>
  );
}

interface Anchor {
  level: number;
  map_handicap: number;
  settled_at: number;
  pass: boolean;
}
const isAnchors = (x: unknown): x is Anchor[] =>
  Array.isArray(x) &&
  x.every((a) => a && typeof a === 'object' && 'level' in a && 'settled_at' in a);
const round1 = (n: number) => (Math.round(n * 10) / 10).toFixed(1);

/** A band as the dashboard shows it under the heading "Band": "band_10" reads "10". */
const bandName = (b: string) => /^band_(.+)$/.exec(b)?.[1] ?? capitalWords(b);

/** A measure's name in words: "fairways_pct" reads "Fairways hit (%)"; one not named, in words. */
const measureName = (m: string) => adminText('sim.measure', { m, w: capitalWords(m) });

interface OutsideRow {
  band: string;
  measure: string;
  got: string;
  target: string;
}

/** One measure outside its target, as the calibration run wrote it: "band_10 fairways_pct 66.79 (target 49)". */
export function outsideRow(o: string): OutsideRow | null {
  const m = /^(\S+) ([a-z0-9_]+) (\S+) \(target ([^)]+)\)$/.exec(o);
  return m ? { band: m[1]!, measure: m[2]!, got: m[3]!, target: m[4]! } : null;
}

/** The measures outside their targets, folded: a table in words, or the run's own lines in words. */
function Outside({ list }: { list: string[] }) {
  const rows = list.map(outsideRow).filter((r): r is OutsideRow => r !== null);
  return (
    <details>
      <summary>{adminText('sim.outside_summary', { n: list.length })}</summary>
      {rows.length === list.length ? (
        <div className="scroll">
          <table className="compact">
            <thead>
              <tr>
                <th scope="col">Band</th>
                <th scope="col">Measure</th>
                <th scope="col">Measured</th>
                <th scope="col">Target</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.band}-${r.measure}`}>
                  <th scope="row">{bandName(r.band)}</th>
                  <td>{measureName(r.measure)}</td>
                  <td>{r.got}</td>
                  <td>{r.target}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <ul className="stops">
          {list.map((o) => (
            <li key={o}>{capitalWords(o)}</li>
          ))}
        </ul>
      )}
    </details>
  );
}

/** The calibration dashboard (10 §8): the stored runs, and the calibration proof's own rows. */
function Calibration({ data }: { data: SimulationData }) {
  const proof = data.proofs.find((p) => p.proof_key === 'calibration');
  const run = proof?.latest ?? null;
  const m =
    run && typeof run.measured === 'object' && run.measured
      ? (run.measured as Record<string, unknown>)
      : null;
  const outside = Array.isArray(m?.outside) ? (m.outside as string[]) : [];
  const anchors = isAnchors(m?.level_anchors) ? m.level_anchors : [];
  return (
    <section aria-labelledby="sim-c" className="block">
      <h3 id="sim-c">{adminText('sim.calibration_heading')}</h3>
      <RunLine run={run} label={adminText('sim.latest_run')} />
      {m && typeof m.rows_gated === 'number' && (
        <p>
          {adminText('sim.calibration_counts', {
            inside: Number(m.rows_inside ?? 0),
            gated: m.rows_gated,
          })}
        </p>
      )}
      {outside.length > 0 && <Outside list={outside} />}
      {anchors.length > 0 && (
        <div className="scroll">
          <table className="compact">
            <caption>{adminText('sim.anchor_caption')}</caption>
            <thead>
              <tr>
                <th scope="col">Level</th>
                <th scope="col">Map says</th>
                <th scope="col">Settled at</th>
                <th scope="col">Result</th>
              </tr>
            </thead>
            <tbody>
              {anchors.map((a) => (
                <tr key={a.level}>
                  <th scope="row">{a.level}</th>
                  <td>{round1(a.map_handicap)}</td>
                  <td>{round1(a.settled_at)}</td>
                  <td>
                    <ProofState state={a.pass ? 'green' : 'red'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {anchors.length > 0 && (
        <p className="actions">
          <button type="button" disabled aria-describedby="sim-anchor-why">
            {adminText('sim.send_anchor')}
          </button>
        </p>
      )}
      {anchors.length > 0 && (
        <p id="sim-anchor-why" className="muted">
          {adminText('sim.send_anchor_off')}
        </p>
      )}
      {data.calibration_runs.length === 0 ? (
        <p className="muted">{adminText('sim.no_calibration_runs')}</p>
      ) : (
        <div className="scroll">
          <table className="compact">
            <caption>
              {adminText('sim.results_caption', { n: data.calibration_results.length })}
            </caption>
            <thead>
              <tr>
                <th scope="col">Band</th>
                <th scope="col">Measure</th>
                <th scope="col">Target</th>
                <th scope="col">Measured</th>
                <th scope="col">Result</th>
              </tr>
            </thead>
            <tbody>
              {data.calibration_results.map((r) => (
                <tr key={`${r.band}-${r.metric}`}>
                  <th scope="row">{bandName(r.band)}</th>
                  <td>{measureName(r.metric)}</td>
                  <td>{r.target ?? '—'}</td>
                  <td>{r.observed ?? '—'}</td>
                  <td>
                    <ProofState
                      state={r.passed === null ? 'not_run' : r.passed ? 'green' : 'red'}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="actions">
        <button type="button" disabled aria-describedby="sim-run-why">
          {adminText('sim.run_now')}
        </button>
      </p>
      <p id="sim-run-why" className="muted">
        {adminText('sim.run_now_off')}
      </p>
    </section>
  );
}

const sameRun = (a: ProofRun | null, b: ProofRun | null) =>
  !!a && !!b && a.run_at === b.run_at && a.size === b.size && a.sim_version === b.sim_version;

function ProofCard({ p }: { p: ProofRow }) {
  return (
    <li className="card">
      <p className="title-row">
        <strong>{proofName(p.proof_key)}</strong>{' '}
        {p.gates_stage_2 && <span className="tag">{adminText('sim.gates_stage_2')}</span>}
      </p>
      <RunLine run={p.counted} label={adminText('sim.counted_run')} />
      {sameRun(p.counted, p.latest) ? (
        <p className="run-line">
          <span className="run-label">{adminText('sim.latest_run')}</span>{' '}
          <span className="muted">{adminText('sim.same_run')}</span>
        </p>
      ) : (
        <RunLine run={p.latest} label={adminText('sim.latest_run')} />
      )}
      {p.latest && <Measured run={p.latest} />}
    </li>
  );
}

/**
 * The sixteen named proofs (31 TST-180; Blueprint 2). A proof for a later stage that has never run is folded
 * away under one line that names each, so the proofs that gate this stage come first.
 */
function Proofs({ data }: { data: SimulationData }) {
  const later = data.proofs.filter((p) => !p.gates_stage_2 && !p.latest && !p.counted);
  const shown = data.proofs.filter((p) => !later.includes(p));
  return (
    <section aria-labelledby="sim-p" className="block">
      <h3 id="sim-p">{adminText('sim.proofs_heading', { n: data.proofs.length })}</h3>
      <p className="muted">{adminText('sim.proofs_intro')}</p>
      <ul className="plain grid">
        {shown.map((p) => (
          <ProofCard key={p.proof_key} p={p} />
        ))}
      </ul>
      {later.length > 0 && (
        <details className="card later">
          <summary>
            {adminText('sim.later_summary', {
              n: later.length,
              names: later.map((p) => proofName(p.proof_key)).join(', '),
            })}
          </summary>
          <ul className="plain grid">
            {later.map((p) => (
              <ProofCard key={p.proof_key} p={p} />
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

/** ADM-095 point 2: the judgement run's twelve shares beside the line. */
function ThoughtLine({ data }: { data: SimulationData }) {
  const line = typeof data.thought_line === 'number' ? data.thought_line : 0.05;
  const j = data.proofs.find((p) => p.proof_key === 'judgement');
  const run = j?.counted ?? j?.latest ?? null;
  const m = run?.measured;
  const shares =
    m && typeof m === 'object' && 'thought_shares' in m
      ? (m as { thought_shares: unknown }).thought_shares
      : null;
  return (
    <section aria-labelledby="sim-t" className="block">
      <h3 id="sim-t">{adminText('sim.thoughts_heading')}</h3>
      <p>{adminText('sim.thoughts_intro', { line: pct(line) })}</p>
      {shares && typeof shares === 'object' ? (
        <ThoughtTable
          shares={shares as Record<string, unknown>}
          line={line}
          caption={adminText('sim.thoughts_caption', { line: pct(line) })}
        />
      ) : (
        <>
          {typeof m === 'string' ? (
            <RunSays words={m} />
          ) : (
            <p className="note">{adminText('sim.thoughts_not_measured')}</p>
          )}
        </>
      )}
    </section>
  );
}

/** 10 §8, the parameter editor: it only ever files a proposal into the approval queue. */
export function ParameterEditor({ sb, keys }: { sb: SupabaseClient; keys: EngineKey[] }) {
  const fair = keys.filter((k) => k.is_fairness).sort((a, b) => a.key.localeCompare(b.key));
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState('');
  const chosen = fair.find((k) => k.key === key);
  return (
    <section aria-labelledby="sim-e" className="block">
      <h3 id="sim-e">{adminText('sim.editor_heading')}</h3>
      <p className="muted">{adminText('sim.editor_intro')}</p>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          let parsed: unknown;
          try {
            parsed = JSON.parse(value.trim());
          } catch {
            return setMsg(messageFor({ ok: false, error: 'bad_value' }));
          }
          const r = await runCommand(sb, 'config.propose', { key, value: parsed }, reason);
          setMsg(r.ok ? adminText('sim.filed') : messageFor(r));
        }}
      >
        <label htmlFor="sim-key">{adminText('sim.editor_key')}</label>
        <select id="sim-key" value={key} onChange={(e) => setKey(e.target.value)} required>
          <option value="">{adminText('sim.editor_choose')}</option>
          {fair.map((k) => (
            <option key={k.key} value={k.key}>
              {k.key}
            </option>
          ))}
        </select>
        {chosen && (
          <p className="muted">
            {chosen.min_value != null && chosen.max_value != null
              ? adminText('config.range', { min: chosen.min_value, max: chosen.max_value })
              : adminText('config.range_none')}
          </p>
        )}
        <label htmlFor="sim-value">{adminText('sim.editor_value')}</label>
        <input
          id="sim-value"
          inputMode="decimal"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          required
        />
        <label htmlFor="sim-reason">{adminText('sim.editor_reason')}</label>
        <input
          id="sim-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
        />
        <p>
          <button className="primary" type="submit">
            {adminText('sim.editor_file')}
          </button>
        </p>
        <p role="status">{msg}</p>
      </form>
    </section>
  );
}

export async function loadSimulation(
  sb: SupabaseClient,
): Promise<{ data: SimulationData } | { error: string }> {
  try {
    const { data, error } = await sb.schema('ops').rpc('admin_simulation');
    if (error || !data) return { error: codeOf(error?.message) };
    return { data: data as SimulationData };
  } catch {
    return { error: 'network' };
  }
}

export function SimulationPage({ sb, owner }: { sb: SupabaseClient; owner: boolean }) {
  const [state, setState] = useState<{ data: SimulationData } | { error: string } | null>(null);
  const [keys, setKeys] = useState<EngineKey[]>([]);
  const retry = () => {
    setState(null);
    void loadSimulation(sb).then(setState);
  };
  useEffect(() => {
    void loadSimulation(sb).then(setState);
    if (owner)
      void sb
        .schema('ops')
        .from('config_keys')
        .select('key,is_fairness,min_value,max_value,owner_file')
        .eq('owner_file', '10')
        .then(({ data }) => setKeys((data ?? []) as EngineKey[]));
  }, [sb, owner]);
  return (
    <>
      <h2>{adminText('sim.heading')}</h2>
      <p className="muted">{adminText('sim.intro')}</p>
      {state === null && (
        <p className="muted" role="status">
          {adminText('sim.loading')}
        </p>
      )}
      {state && 'error' in state && (
        <>
          <p className="warn" role="alert">
            {messageFor({ ok: false, error: state.error })}
          </p>
          <p>
            <button type="button" onClick={retry}>
              {adminText('sim.try_again')}
            </button>
          </p>
        </>
      )}
      {state && 'data' in state && (
        <>
          <Versions data={state.data} />
          <Calibration data={state.data} />
          <ThoughtLine data={state.data} />
          <Proofs data={state.data} />
          {owner && <ParameterEditor sb={sb} keys={keys} />}
        </>
      )}
    </>
  );
}
